import React, { useState, useEffect, useCallback } from "react";
import { Paper, Typography, Button, Grid } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import axios from "axios";
import ProfileCard from "../components/ProfileCard";
import ProfileDialog from "../components/ProfileDialog";

const ProfileManagement = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [profiles, setProfiles] = useState([]);
  const [newProfileDialogOpen, setNewProfileDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: "",
    allowedPackages: [],
    packagePricing: {},
  });
  const [loading, setLoading] = useState(false);
  const { token } = useSelector((state) => state.auth);

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get("/api/profiles", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfiles(response.data);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      enqueueSnackbar(
        error.response?.data?.message || t("errors.fetchProfilesFailed"),
        { variant: "error" }
      );
    }
  }, [token, enqueueSnackbar, t]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleCreateProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.post("/api/profiles", profileForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        enqueueSnackbar(t("auth.profile.profileCreated"), {
          variant: "success",
        });
        await fetchProfiles();
        setNewProfileDialogOpen(false);
        setProfileForm({
          name: "",
          allowedPackages: [],
          packagePricing: {},
        });
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || t("errors.createProfileFailed"),
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    try {
      await axios.delete(`/api/profiles/${profileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      enqueueSnackbar(t("auth.profile.profileDeleted"), { variant: "success" });
      await fetchProfiles();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || t("errors.deleteProfileFailed"),
        { variant: "error" }
      );
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t("auth.profile.manageProfiles")}
      </Typography>

      <Grid container spacing={2}>
        {profiles.map((profile) => (
          <Grid item xs={12} md={6} key={profile.id}>
            <ProfileCard
              profile={profile}
              onDelete={() => handleDeleteProfile(profile.id)}
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setNewProfileDialogOpen(true)}
          >
            {t("auth.profile.addProfile")}
          </Button>
        </Grid>
      </Grid>

      <ProfileDialog
        open={newProfileDialogOpen}
        onClose={() => setNewProfileDialogOpen(false)}
        onSubmit={handleCreateProfile}
        loading={loading}
        formData={profileForm}
        setFormData={setProfileForm}
      />
    </Paper>
  );
};

export default ProfileManagement;
